import React, { useEffect, useMemo, useState } from "react";
import { imgSrc, imgSet } from "@/lib/img";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import JsonLd from "@/components/site/JsonLd";
import { useLang } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";
import { graph, breadcrumbNode, itemListNode } from "@/lib/schema";
import api, { resolveMediaUrl } from "@/lib/api";

export default function Destinations() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useSeo({ title: t("seo.dest.title"), description: t("seo.dest.desc"), path: "/destinazioni" });

  useEffect(() => {
    api.get("/destinations").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const jsonLd = useMemo(
    () =>
      graph([
        breadcrumbNode([{ name: "Home", path: "/" }, { name: t("nav.destinations"), path: "/destinazioni" }], lang),
        itemListNode({ lang, items: items.map((d) => ({ name: d.name, path: `/destinazioni/${d.slug}` })) }),
      ]),
    [items, lang, t]
  );

  return (
    <div className="bg-obsidian text-cream min-h-screen">
      <Nav />
      <JsonLd id="jsonld-destinations" data={jsonLd} />

      <section className="pt-32 sm:pt-44 pb-12">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow mb-3">{t("dest.eyebrow")}</p>
            <h1 className="font-serif font-semibold text-5xl sm:text-7xl leading-[0.95]" data-testid="destinations-title">{t("dest.title")}</h1>
            <p className="mt-6 max-w-2xl text-cream2 leading-relaxed">{t("dest.intro")}</p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 sm:pb-32" data-testid="destinations-grid">
        <div className="container-x grid grid-cols-1 md:grid-cols-2 gap-px bg-copper/20 border border-copper/20">
          {items.map((d, i) => (
            <Reveal key={d.slug} delay={(i % 2) * 0.08} className="bg-obsidian">
              <Link to={`/destinazioni/${d.slug}`} className="group block h-full" data-testid={`destination-card-${d.slug}`}>
                <div className="aspect-[16/10] overflow-hidden">
                  {d.cover && (
                    <img
                      src={imgSrc(resolveMediaUrl(d.cover), 1200)}
                      srcSet={imgSet(resolveMediaUrl(d.cover), [400, 800, 1200])}
                      sizes="(max-width: 768px) 100vw, 50vw"
                      alt={d.name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover opacity-85 group-hover:opacity-100 transition-all duration-[1200ms] group-hover:scale-[1.04]"
                    />
                  )}
                </div>
                <div className="p-7 sm:p-9">
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-sand flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5 text-copper" /> {d.region ? `${d.region} · ` : ""}{d.country}
                  </p>
                  <h2 className="font-serif text-3xl sm:text-4xl mt-3 group-hover:text-copperlight transition-colors">{d.name}</h2>
                  <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-copper flex items-center gap-2">
                    {d.tours_count} {d.tours_count === 1 ? t("dest.oneTrip") : t("dest.manyTrips")}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        {loaded && items.length === 0 && <p className="text-sand text-center py-20">{t("dest.empty")}</p>}
      </section>

      <Footer />
    </div>
  );
}
